import { Alert, Collapse, Group, IconButton, iconSizes, Loader, Stack, Table, Title, Tooltip } from '@common/ui'
import { useNavigate } from 'react-router-dom'
import { IconArrowLeft } from '@common/ui/icons'
import { useEffect, useMemo, useState } from 'react'
import { com } from '@eidu/web'
import useEntityTypes from '../../io/useEntityTypes'
import usePaginatedPermissionGrants from '../../io/usePaginatedPermissionGrants'
import useHasGlobalPermissions from './useHasGlobalPermissions'
import PermissionError from '../entity/PermissionError'
import useEntitiesOfType from '../../io/useEntitiesOfType'
import { useEntitiesByIds } from '../../io/useEntities'
import { EMPTY_ENTITY_TYPES } from '../../io/useEntityTypes'
import { usePermissionPoliciesByIds } from '../../io/usePermissionPolicies'
import EqualityHashMap from '../../util/EqualityHashMap'
import { findEntityTypeByFieldId, findFieldById } from '../../util/EntityTypeExtensions'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import EntitySelectorChips from '../../components/input/EntitySelectorChips'
import UserTypesWithoutColumnWarning from '../../components/permissions/visualizer/UserTypesWithoutColumnWarning'
import VisibleColumnsPicker from '../../components/permissions/visualizer/VisibleColumnsPicker'
import VisualizerTableRow from '../../components/permissions/visualizer/VisualizerTableRow'
import {
  buildColumnHeaders,
  buildRowHeaders,
  collectSelectorFieldIds,
} from '../../components/permissions/visualizer/utils'
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import EntityId = com.eidu.entity.EntityId
import EntitySelector = com.eidu.entity.selector.EntitySelector
import EntitySelectorPermission = com.eidu.entity.permission.EntitySelectorPermission
import FieldType = com.eidu.entity.field.FieldType
import GlobalAction = com.eidu.entity.permission.GlobalAction
import PermissionPolicyId = com.eidu.entity.permission.PermissionPolicyId

const AccessControlVisualizerPage = () => {
  const navigate = useNavigate()
  const { entityTypes: entityTypesMap } = useEntityTypes()
  const entityTypes = useMemo(() => [...(entityTypesMap?.values() ?? [])], [entityTypesMap])
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(new Set())

  const { grants, hasMore, loading: loadingGrants, loadMore } = usePaginatedPermissionGrants({ pageSize: 200 })
  useEffect(() => {
    if (hasMore && !loadingGrants) loadMore()
  }, [hasMore, loadingGrants, loadMore])
  const grantsError: unknown = undefined

  const { hasPermission, loading: permissionsLoading } = useHasGlobalPermissions(GlobalAction.ManagePermissionGrants)
  const hasManagePermissionGrantsPermission = hasPermission(GlobalAction.ManagePermissionGrants)

  const userSelectors = useMemo(() => grants.map((grant) => grant.permissionGrant.userSelector), [grants])

  const selectorFieldIds = useMemo(() => userSelectors.flatMap(collectSelectorFieldIds), [userSelectors])

  const referencableEntityTypeIds = useMemo(() => {
    const ids = new Map<string, EntityTypeId>()
    selectorFieldIds.forEach((fieldId) => {
      const entityType = findEntityTypeByFieldId(entityTypes, fieldId)
      if (!entityType) return
      const field = findFieldById(entityType, fieldId)
      if (!field || field.type !== FieldType.Reference) return
      Array.from(field.validReferenceTypes?.asJsReadonlySetView() ?? []).forEach((typeId) => {
        ids.set(typeId.toString(), typeId)
      })
    })
    return Array.from(ids.values())
  }, [entityTypes, selectorFieldIds])

  const referencedEntitiesByTypeId =
    useEntitiesOfType({
      typeIds: referencableEntityTypeIds,
      allTypes: entityTypesMap ?? EMPTY_ENTITY_TYPES,
      pageSize: 100,
    }) ?? new Map()

  const singleTargetIds = useMemo(
    () =>
      grants.flatMap((grant) =>
        [...grant.permissionGrant.permissions.asJsReadonlySetView()]
          .filter(
            (p): p is EntitySelectorPermission =>
              p instanceof EntitySelectorPermission && p.targets instanceof EntitySelector.Single
          )
          .map((p) => (p.targets as EntitySelector.Single).entityId)
      ),
    [grants]
  )
  const { entities: singleTargetEntities } = useEntitiesByIds(singleTargetIds)

  const entitiesByTypeId = useMemo(() => {
    const merged = new EqualityHashMap<EntityTypeId, EqualityHashMap<EntityId, EntityWithLabelAndRelated>>(
      [...referencedEntitiesByTypeId.entries()].map(([typeId, entities]) => [typeId, new EqualityHashMap(entities)])
    )
    for (const entity of singleTargetEntities?.values() ?? []) {
      if (!entity) continue
      const typeId = entity.entity.typeId
      const inner = merged.get(typeId) ?? new EqualityHashMap<EntityId, EntityWithLabelAndRelated>()
      inner.set(entity.entity.id, entity)
      merged.set(typeId, inner)
    }
    return merged
  }, [referencedEntitiesByTypeId, singleTargetEntities])

  const referencedPolicyIds = useMemo(() => {
    const map = new EqualityHashMap<PermissionPolicyId, PermissionPolicyId>()
    for (const grant of grants) {
      for (const id of grant.permissionGrant.policyIds.asJsReadonlySetView()) map.set(id, id)
    }
    return [...map.values()]
  }, [grants])
  const referencedPoliciesQuery = usePermissionPoliciesByIds(referencedPolicyIds)
  const policiesById = useMemo(() => {
    const map = new Map<string, (typeof referencedPoliciesQuery.policies)[number]>()
    for (const policy of referencedPoliciesQuery.policies) map.set(policy.policyId.toString(), policy)
    return map
  }, [referencedPoliciesQuery.policies])

  const columnHeaders = useMemo(
    () => buildColumnHeaders(grants, entityTypes, entitiesByTypeId, policiesById),
    [grants, entityTypes, entitiesByTypeId, policiesById]
  )

  const columnByKey = useMemo(() => new Map(columnHeaders.map((c) => [c.id, c])), [columnHeaders])

  const visibleColumnHeaders = useMemo(
    () => columnHeaders.filter((c) => !hiddenColumns.has(c.id)),
    [columnHeaders, hiddenColumns]
  )

  const rowHeaders = useMemo(() => buildRowHeaders(entityTypes), [entityTypes])

  if (permissionsLoading) {
    return (
      <Group justify="center" p="xl">
        <Loader />
      </Group>
    )
  }

  if (!hasManagePermissionGrantsPermission) {
    return <PermissionError missingPermittedActions={[GlobalAction.ManagePermissionGrants]} />
  }

  return (
    <Stack gap="md" p="lg">
      <Collapse expanded={!!grantsError}>
        <Alert variant="filled" severity="negative" mb="md">
          {String(grantsError)}
        </Alert>
      </Collapse>
      <UserTypesWithoutColumnWarning
        entityTypes={entityTypes}
        columnHeaders={columnHeaders}
        onGrantPermission={(typeId) =>
          navigate('/access-control/new', {
            state: { initialUserTypeId: typeId },
          })
        }
      />
      <div
        style={{
          border: '1px solid var(--trellis-color-outline)',
          borderRadius: 'var(--trellis-radius-sm)',
          overflow: 'hidden',
        }}
      >
        <Group
          gap="xs"
          px="md"
          py="xs"
          style={{
            backgroundColor: 'var(--trellis-color-surface-variant)',
            color: 'var(--trellis-color-on-surface-variant)',
            borderBottom: '1px solid var(--trellis-color-outline)',
          }}
        >
          <Tooltip label="Back to Access Control">
            <IconButton
              size="sm"
              onClick={() => navigate('/access-control')}
              style={{ color: 'inherit' }}
              aria-label="Back to Access Control"
            >
              <IconArrowLeft size={iconSizes.sm} stroke={1.5} />
            </IconButton>
          </Tooltip>
          <Title order={4} size="md">
            Access Control Visualizer
          </Title>
        </Group>
        {columnHeaders.length > 0 && (
          <VisibleColumnsPicker
            columnHeaders={columnHeaders}
            visibleColumnHeaders={visibleColumnHeaders}
            onChange={(visibleIds) =>
              setHiddenColumns(new Set(columnHeaders.filter((c) => !visibleIds.has(c.id)).map((c) => c.id)))
            }
          />
        )}
        <div style={{ overflowX: 'auto' }}>
          <Table stickyHeader withColumnBorders verticalSpacing={2} horizontalSpacing={8} style={{ width: 'auto' }}>
            <Table.Thead>
              <Table.Tr style={{ backgroundColor: 'var(--trellis-color-surface-variant)' }}>
                <Table.Th style={{ width: 220, minWidth: 220 }} />
                {visibleColumnHeaders.map((column) => (
                  <Table.Th key={column.id} style={{ minWidth: 140, textAlign: 'left' }}>
                    <EntitySelectorChips
                      selector={column.selector}
                      entityTypes={entityTypesMap ?? EMPTY_ENTITY_TYPES}
                      entitiesByTypeId={entitiesByTypeId}
                      direction="column"
                    />
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rowHeaders.map((row) => (
                <VisualizerTableRow
                  key={row.id}
                  row={row}
                  visibleColumnHeaders={visibleColumnHeaders}
                  totalColumnCount={columnHeaders.length}
                  columnByKey={columnByKey}
                  entityTypes={entityTypes}
                  entitiesByTypeId={entitiesByTypeId}
                />
              ))}
            </Table.Tbody>
          </Table>
        </div>
      </div>
      {loadingGrants && (
        <Group justify="center" p="md">
          <Loader />
        </Group>
      )}
    </Stack>
  )
}

export default AccessControlVisualizerPage
